import {defineStore} from 'pinia';
import {mapPromotions} from '~/utils/mapper-promotions.js';

const GROUP_PRICING_CACHE_TTL = 5 * 60 * 1000;
const groupPricingRequests = process.client ? new Map() : null;
let promotionPricingRequest = null;

export const usePricingStore = defineStore('pricingStore', {
  state: () => ({
    promotions: [],
    promotionsLoaded: false,
    promotionsPending: false,
    groupPricingCache: {}
  }),

  actions: {
    async getPromotions({forceRefresh = false} = {}) {
      if (!forceRefresh && this.promotionsLoaded) {
        return this.promotions;
      }

      if (process.client && promotionPricingRequest) {
        return promotionPricingRequest;
      }

      this.promotionsPending = true;

      const request = usePricingApi().getPromotionPricing()
        .then((promotions) => {
          this.promotions = promotions || [];
          this.promotionsLoaded = true;

          return this.promotions;
        })
        .finally(() => {
          this.promotionsPending = false;

          if (process.client) {
            promotionPricingRequest = null;
          }
        });

      if (process.client) {
        promotionPricingRequest = request;
      }

      return request;
    },

    async getGroupProducts({
      groupSlug,
      includeDescriptions = false,
      forceRefresh = false
    }) {
      const groupCacheKey = `${groupSlug}-${includeDescriptions ? 'with-descriptions' : 'without-descriptions'}`;
      const cachedGroup = this.groupPricingCache[groupCacheKey];
      const cacheIsValid = cachedGroup && Date.now() - cachedGroup.fetchedAt < GROUP_PRICING_CACHE_TTL;

      if (!forceRefresh && cacheIsValid) {
        return cachedGroup.products;
      }

      if (groupPricingRequests?.has(groupCacheKey)) {
        return groupPricingRequests.get(groupCacheKey);
      }

      const request = Promise.all([
        usePricingApi().getGroupProducts({
          groupSlug,
          includeDescriptions
        }),
        usePricingApi().getGroupPromotions({groupSlug})
      ]).then(([products, promotions]) => {
        const mappedProducts = mapPromotions({products, promotions});

        this.groupPricingCache[groupCacheKey] = {
          products: mappedProducts,
          fetchedAt: Date.now()
        };

        return mappedProducts;
      }).finally(() => {
        groupPricingRequests?.delete(groupCacheKey);
      });

      groupPricingRequests?.set(groupCacheKey, request);
      return request;
    }
  }
});
